import { useMemo, type ComponentProps } from "react"
import { format } from "date-fns"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatTrendDetail } from "@/v2-backoffice-client/usage/usage.constants"
import UsageReportBarList from "@/v2-backoffice-client/usage/report/UsageReportBarList"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageReportNotes from "@/v2-backoffice-client/usage/report/UsageReportNotes"
import UsageReportProse from "@/v2-backoffice-client/usage/report/UsageReportProse"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import { formatLicenseDeniedReason } from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import UsageMcpWeekChart from "@/v2-backoffice-client/usage/mcp/UsageMcpWeekChart"
import {
  formatMcpClient,
  formatMcpTool,
  MCP_CLIENT_LIST_LIMIT,
  MCP_REASON_LIST_LIMIT,
  MCP_REPORT_NOTES,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import { deriveMcpPortfolioStory } from "@/v2-backoffice-client/usage/mcp/usage.mcp.story"
import {
  deriveMcpToolGroups,
  mcpTrendPct,
  mergeMcpCounts,
  type UsageMcpCountRow,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import PortfolioMcpCustomersTable from "@/v2-backoffice-client/portfolio/PortfolioMcpCustomersTable"
import PortfolioMcpConcentration from "@/v2-backoffice-client/portfolio/PortfolioMcpConcentration"
import PortfolioMcpDatasetSection from "@/v2-backoffice-client/portfolio/PortfolioMcpDatasetSection"
import PortfolioMcpPeopleTable from "@/v2-backoffice-client/portfolio/PortfolioMcpPeopleTable"
import PortfolioMcpRefusedTable from "@/v2-backoffice-client/portfolio/PortfolioMcpRefusedTable"
import { usePortfolioMcpData } from "@/v2-backoffice-client/portfolio/portfolio.mcp.data"
import {
  selectMcpRefusedCustomers,
  type PortfolioMcpCustomer,
  type PortfolioMcpMember,
  deriveMcpConcentration,
} from "@/v2-backoffice-client/portfolio/portfolio.mcp.utils"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"

export type PortfolioMcpSectionProps = ComponentProps<"div"> & {
  /** Resolved server-side scope (facets resolved to ids); null = everyone. */
  customerIds: string[] | null
  excludeCustomerIds: string[]
  excludeUserIds: number[]
}

const PLACEPOINT_MCP = "mcp-placepoint"
const DATASET_MCP = "mcp-dataset"
const TOP_CUSTOMERS_LIMIT = 10
const TOP_TOOLS_LIMIT = 15

const EMPTY_COUNTS: UsageMcpCountRow[] = []
const EMPTY_CUSTOMERS: PortfolioMcpCustomer[] = []
const EMPTY_MEMBERS: PortfolioMcpMember[] = []

// The MCP tab is the one-off "Kundenes bruk av Placepoint MCP" report as a
// living page: the same chapters, each a section with an eyebrow, a title
// and a plain paragraph, its numbers grouped into cards. Everything is
// aggregated server-side (acc.McpUsageDaily, AIP-47) for one server at a
// time; the page derives the prose and renders. Placepoint's own traffic is
// reported as internal calls, never mixed in.
function PortfolioMcpSection(props: PortfolioMcpSectionProps) {
  const {
    customerIds,
    excludeCustomerIds,
    excludeUserIds,
    className,
    ...divProps
  } = props

  const scope = { customerIds, excludeCustomerIds, excludeUserIds }
  const { mcp, loading, error } = usePortfolioMcpData({
    appCode: PLACEPOINT_MCP,
    ...scope,
  })
  const dataset = usePortfolioMcpData({ appCode: DATASET_MCP, ...scope })

  const { data: licenseStatusData } = useLicenseStatusesQuery()
  const licenseStatusNamesByCode = useMemo(
    () =>
      new Map(
        (licenseStatusData?.getLicenseStatuses ?? []).map((status) => [
          status.Code,
          status.Name,
        ]),
      ),
    [licenseStatusData?.getLicenseStatuses],
  )

  const summary = mcp?.Summary ?? null
  const weeks = mcp?.Weeks ?? []
  const customers = mcp?.Customers ?? EMPTY_CUSTOMERS
  const members = mcp?.Members ?? EMPTY_MEMBERS
  const tools = useMemo(
    () => mergeMcpCounts(mcp?.Tools ?? EMPTY_COUNTS),
    [mcp?.Tools],
  )
  const clients = useMemo(
    () => mergeMcpCounts(mcp?.Clients ?? EMPTY_COUNTS),
    [mcp?.Clients],
  )
  const deniedReasons = useMemo(
    () => mergeMcpCounts(mcp?.DeniedReasons ?? EMPTY_COUNTS),
    [mcp?.DeniedReasons],
  )
  const groups = useMemo(() => deriveMcpToolGroups(tools), [tools])
  const refused = useMemo(
    () => selectMcpRefusedCustomers(customers),
    [customers],
  )
  const story = useMemo(
    () =>
      summary
        ? deriveMcpPortfolioStory({
            summary,
            weeks,
            customers,
            tools,
            clients,
            groups,
            datasetSummary: dataset.mcp?.Summary,
            datasetCustomers: dataset.mcp?.Customers,
          })
        : null,
    [summary, weeks, customers, tools, clients, groups, dataset.mcp],
  )

  const topCustomers = useMemo(
    () =>
      customers
        .filter((customer) => customer.Calls28d > 0)
        .toSorted((a, b) => b.Calls28d - a.Calls28d)
        .map((customer) => ({
          key: customer.ExternalCustomerId,
          label: customer.Name,
          value: customer.Calls28d,
          extra: `${customer.Users28d} ${customer.Users28d === 1 ? "person" : "people"}`,
        })),
    [customers],
  )
  const clientsByPeople = useMemo(
    () =>
      clients
        .filter((client) => client.Users28d > 0)
        .toSorted((a, b) => b.Users28d - a.Users28d)
        .map((client) => ({
          key: client.Key,
          label: formatMcpClient(client.Key),
          value: client.Users28d,
          extra: `${formatNumber(client.Calls28d)} calls`,
        })),
    [clients],
  )

  const firstDay = summary?.FirstDay ? new Date(summary.FirstDay) : null
  const trendPct = summary
    ? mcpTrendPct(summary.Calls28d, summary.CallsPrev28d ?? 0)
    : null
  const peopleCustomers = new Set(members.map((m) => m.ExternalCustomerId)).size

  return (
    <div className={cn("usage-report", className)} {...divProps}>
      <UsageReportSection
        eyebrow="Placepoint MCP"
        title="How customers use the Placepoint MCP"
        lead={
          <>
            Tool calls customers' people make against the Placepoint MCP from an
            AI client such as Claude, ChatGPT or Copilot. One question usually
            fans out into several calls: a search first, then documents, then a
            map or the land register.
            {firstDay && (
              <>
                {" "}
                The figures run from the first tracked call on{" "}
                {format(firstDay, "d MMMM yyyy")} to today;
              </>
            )}{" "}
            Placepoint's own people are kept apart as internal calls.
          </>
        }
      >
        <ErrorMessage error={error} />

        {loading && !mcp ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !summary ? (
          <p className="usage-report-empty">
            No customer in scope has called the Placepoint MCP yet. Rows appear
            the morning after the first tool call, once the nightly usage run
            has gone.
          </p>
        ) : (
          <>
            <div className="usage-report-kpis usage-report-kpis--six">
              <UsageReportKpi
                label="Tool calls"
                value={formatNumber(summary.CallsTotal)}
                sub={[
                  <>
                    {formatNumber(summary.Calls28d)} in the last 28 days{" "}
                    <UsageTrendBadge
                      trendPct={trendPct}
                      detail={formatTrendDetail(
                        summary.Calls28d,
                        summary.CallsPrev28d ?? 0,
                      )}
                    />
                  </>,
                  `${formatNumber(summary.Calls7d)} in the last 7 days`,
                ]}
              />
              <UsageReportKpi
                label="People"
                value={formatNumber(summary.UsersTotal)}
                sub={[
                  `${formatNumber(summary.Users28d)} active in the last 28 days`,
                  `${formatNumber(summary.Users7d)} in the last 7 days`,
                ]}
              />
              <UsageReportKpi
                label="Customers"
                value={formatNumber(summary.CustomersTotal ?? 0)}
                sub={[
                  `${formatNumber(summary.Customers28d ?? 0)} active in the last 28 days`,
                  `${formatNumber(summary.NewCustomers28d ?? 0)} started in the last 28 days`,
                ]}
              />
              <UsageReportKpi
                label="A working day"
                value={
                  summary.MedianCallsPerPersonDay28d != null
                    ? `${formatNumber(Math.round(summary.MedianCallsPerPersonDay28d))} calls`
                    : "—"
                }
                sub={[
                  summary.MedianToolsPerPersonDay28d != null
                    ? `across ${formatNumber(Math.round(summary.MedianToolsPerPersonDay28d))} different tools`
                    : "—",
                  "for a typical person on a day they use it",
                ]}
              />
              <UsageReportKpi
                label="Turned away"
                tone={summary.Denied28d > 0 ? "warning" : "default"}
                value={
                  summary.Denied28d > 0 ? (
                    formatNumber(summary.Denied28d)
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )
                }
                sub={
                  summary.Denied28d > 0
                    ? [
                        `${summary.DeniedUsers28d} ${summary.DeniedUsers28d === 1 ? "person" : "people"} at ${summary.DeniedCustomers28d ?? 0} customer${summary.DeniedCustomers28d === 1 ? "" : "s"}`,
                        "tried without a seat, last 28 days",
                      ]
                    : "nobody refused in the last 28 days"
                }
              />
              <UsageReportKpi
                label="Internal calls"
                value={formatNumber(summary.InternalCallsTotal ?? 0)}
                sub={[
                  `${formatNumber(summary.InternalCalls28d ?? 0)} in the last 28 days`,
                  "Placepoint's own people, not in the figures above",
                ]}
              />
            </div>

            {story && (
              <UsageReportProse title="In short" paragraphs={story.summary} />
            )}
          </>
        )}
      </UsageReportSection>

      {summary && story && (
        <>
          <UsageReportSection
            eyebrow="Week by week"
            title="How use has developed"
            lead="Tool calls per week, Monday to Sunday, with the people and customers behind them. The current week is still running and is drawn lighter."
          >
            <UsageReportCard>
              <UsageMcpWeekChart weeks={weeks} showCustomers />
            </UsageReportCard>
          </UsageReportSection>

          <UsageReportSection
            eyebrow="Customers"
            title="Who uses it most"
            lead={`${customers.length} ${customers.length === 1 ? "customer has" : "customers have"} called the Placepoint MCP. The chart ranks the last 28 days; the table carries every customer with the MCP license it holds, its people, totals and the AI clients it connects from. A name opens the customer's own MCP page.`}
          >
            <div className="usage-report-grid usage-report-grid--two">
              <UsageReportCard
                title="Top customers, last 28 days"
                caption="Tool calls in the last 28 days and the people behind them."
              >
                <UsageReportBarList
                  rows={topCustomers}
                  limit={TOP_CUSTOMERS_LIMIT}
                  labelWidth="w-48"
                  emptyText="No tool calls in the last 28 days."
                />
              </UsageReportCard>
              <PortfolioMcpConcentration
                concentration={deriveMcpConcentration(
                  customers,
                  summary.Calls28d,
                )}
              />
              <UsageReportCard title="Every customer" wide>
                <PortfolioMcpCustomersTable
                  customers={customers}
                  licenseStatusNamesByCode={licenseStatusNamesByCode}
                />
              </UsageReportCard>
            </div>
          </UsageReportSection>

          <UsageReportSection
            eyebrow="License gate"
            title="Who is knocking on the door"
            lead="People who tried to use the MCP without a seat on their customer's MCP license and were turned away. Each attempt is a refusal before any tool ran; AI clients retry on their own, so attempts measure persistence, not questions. Every row is a seat to sell."
          >
            {refused.length === 0 ? (
              <p className="usage-report-empty">
                Nobody was turned away in the last 28 days.
              </p>
            ) : (
              <div className="usage-report-grid usage-report-grid--two">
                <UsageReportProse
                  title="Where they stand"
                  paragraphs={story.gate}
                  tone="warning"
                />
                <UsageReportCard
                  title="Why they were refused"
                  caption="Attempts in the last 28 days by reason, and the people behind them."
                >
                  <UsageReportBarList
                    tone="warning"
                    rows={deniedReasons.map((reason) => ({
                      key: reason.Key,
                      label: formatLicenseDeniedReason(reason.Key),
                      value: reason.Calls28d,
                      extra: `${reason.Users28d} ${reason.Users28d === 1 ? "person" : "people"}`,
                    }))}
                    limit={MCP_REASON_LIST_LIMIT}
                    labelWidth="w-56"
                    emptyText="Nobody refused."
                  />
                </UsageReportCard>
                <UsageReportCard title="Every customer turned away" wide>
                  <PortfolioMcpRefusedTable
                    customers={refused}
                    licenseStatusNamesByCode={licenseStatusNamesByCode}
                  />
                </UsageReportCard>
              </div>
            )}
          </UsageReportSection>

          <UsageReportSection
            eyebrow="Usage"
            title="What customers use the MCP for"
            lead="Tool calls from customers in the last 28 days, by area and by individual tool, and the AI clients they come from."
          >
            <div className={usageKpiGridClass(groups.length)}>
              {groups.map((group) => (
                <UsageReportKpi
                  key={group.group}
                  label={group.label}
                  value={formatNumber(group.calls)}
                  sub={`${Math.round(group.share * 100)} % of the calls`}
                />
              ))}
            </div>
            <div className="usage-report-grid usage-report-grid--two">
              <UsageReportCard
                title="Most used tools"
                caption={`The ${Math.min(TOP_TOOLS_LIMIT, tools.length)} most used tools, calls from customers in the last 28 days.`}
              >
                <UsageReportBarList
                  rows={tools.map((tool) => ({
                    key: tool.Key,
                    label: formatMcpTool(tool.Key),
                    value: tool.Calls28d,
                    extra: `${tool.Users28d} ${tool.Users28d === 1 ? "person" : "people"}`,
                  }))}
                  limit={TOP_TOOLS_LIMIT}
                  labelWidth="w-48"
                  emptyText="No tool calls in the last 28 days."
                />
              </UsageReportCard>
              <div className="flex flex-col gap-4">
                <UsageReportCard
                  title="AI clients"
                  caption="People who connected through each client in the last 28 days. A person can use more than one."
                >
                  <UsageReportBarList
                    rows={clientsByPeople}
                    limit={MCP_CLIENT_LIST_LIMIT}
                    labelWidth="w-48"
                    emptyText="No client has called a tool in the last 28 days."
                  />
                </UsageReportCard>
                <UsageReportProse
                  title="The clients"
                  paragraphs={story.clients}
                />
                <UsageReportProse title="The work" paragraphs={story.usage} />
              </div>
            </div>
          </UsageReportSection>
        </>
      )}

      <PortfolioMcpDatasetSection
        mcp={dataset.mcp}
        loading={dataset.loading}
        error={dataset.error}
        story={story?.dataset ?? []}
      />

      {members.length > 0 && (
        <UsageReportSection
          eyebrow="People"
          title="Everyone on the Placepoint MCP"
          lead={`${members.length} ${members.length === 1 ? "person" : "people"} at ${peopleCustomers} ${peopleCustomers === 1 ? "customer" : "customers"}, grouped by customer: calls since their first, the last 28 and 7 days, days with a call, and the AI clients each connects from.`}
        >
          <UsageReportCard>
            <details className="group">
              <summary className="cursor-pointer select-none text-sm font-medium text-muted-foreground hover:text-foreground">
                <span className="group-open:hidden">Show everyone</span>
                <span className="hidden group-open:inline">Hide the list</span>
              </summary>
              <PortfolioMcpPeopleTable members={members} className="mt-3" />
            </details>
          </UsageReportCard>
        </UsageReportSection>
      )}

      <UsageReportCard>
        <UsageReportNotes notes={MCP_REPORT_NOTES} />
      </UsageReportCard>
    </div>
  )
}

export default PortfolioMcpSection
